import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { parseYouTubeId, youtubeThumbnailUrl } from "@/lib/youtube";

export function slugifyTitle(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_~>#-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function buildExcerpt(bodyMarkdown: string, excerpt?: string | null): string {
  if (excerpt?.trim()) return excerpt.trim();
  const plain = stripMarkdown(bodyMarkdown);
  if (plain.length <= 160) return plain;
  return `${plain.slice(0, 157).trim()}…`;
}

export type SlugTable = "blog_posts" | "practice_library_entries";

export async function ensureUniqueSlug(
  supabase: SupabaseClient<Database>,
  table: SlugTable,
  baseSlug: string,
  excludeId?: string,
  fallback = "post",
): Promise<string> {
  const root = baseSlug || fallback;
  let slug = root;
  let suffix = 1;

  while (true) {
    const { data } = await supabase.from(table).select("id").eq("slug", slug).maybeSingle();
    if (!data || (excludeId && data.id === excludeId)) return slug;
    suffix += 1;
    slug = `${root}-${suffix}`;
  }
}

const MARKDOWN_IMAGE_PATTERN = /!\[([^\]]*)\]\(([^)]+)\)/g;

export function extractMarkdownImageUrls(markdown: string): { url: string; alt: string }[] {
  const images: { url: string; alt: string }[] = [];
  const pattern = new RegExp(MARKDOWN_IMAGE_PATTERN.source, "g");
  let match = pattern.exec(markdown);
  while (match) {
    const url = match[2]?.trim();
    if (url) {
      images.push({ url, alt: match[1]?.trim() ?? "" });
    }
    match = pattern.exec(markdown);
  }
  return images;
}

export function extractYouTubeIdsFromMarkdown(markdown: string): string[] {
  const ids: string[] = [];
  for (const line of markdown.split("\n")) {
    const trimmed = line.trim();
    const bareId = parseYouTubeId(trimmed);
    if (bareId) {
      ids.push(bareId);
      continue;
    }

    const linkPattern = /\[[^\]]*\]\(([^)]+)\)/g;
    let linkMatch = linkPattern.exec(trimmed);
    while (linkMatch) {
      const linkId = parseYouTubeId(linkMatch[1] ?? "");
      if (linkId) ids.push(linkId);
      linkMatch = linkPattern.exec(trimmed);
    }
  }
  return ids;
}

export type ResolveBlogShareImageInput = {
  shareImageUrl?: string | null;
  bodyMarkdown: string;
  defaultImageUrl: string;
};

export function resolveBlogShareImageUrl(input: ResolveBlogShareImageInput): string {
  const chosen = input.shareImageUrl?.trim();
  if (chosen) return chosen;

  const firstImage = extractMarkdownImageUrls(input.bodyMarkdown)[0];
  if (firstImage?.url) return firstImage.url;

  const firstYouTubeId = extractYouTubeIdsFromMarkdown(input.bodyMarkdown)[0];
  if (firstYouTubeId) return youtubeThumbnailUrl(firstYouTubeId);

  return input.defaultImageUrl;
}
